import './style.css'

import {
  Application,
  Asset,
  BoundingBox,
  CameraComponent,
  Color,
  Entity,
  FILLMODE_FILL_WINDOW,
  PROJECTION_PERSPECTIVE,
  RESOLUTION_AUTO,
  Vec3
} from 'playcanvas'

/* ------------------------------------------------------------------ */
/* 类型与常量                                                           */
/* ------------------------------------------------------------------ */

type FormatId = 'sog' | 'ply' | 'glb' | 'gltf'

const EXT_TO_FORMAT: Record<string, FormatId> = {
  sog: 'sog',
  ply: 'ply',
  glb: 'glb',
  gltf: 'gltf'
}

const FORMAT_LABEL: Record<FormatId, string> = {
  sog: 'SOG 高斯泼溅 (.sog)',
  ply: '高斯泼溅点云 (.ply)',
  glb: 'glTF 二进制模型 (.glb)',
  gltf: 'glTF 模型 (.gltf)'
}

const FOV = 55 // 垂直视场角(度)
const RAD = Math.PI / 180
const TMP_BOX = new BoundingBox()

/* ------------------------------------------------------------------ */
/* 模块状态                                                             */
/* ------------------------------------------------------------------ */

let app: Application
let cameraEntity: Entity
let camera: CameraComponent
let contentRoot: Entity // 所有已加载模型挂载于此

let jobSeq = 0 // 加载序号，用于丢弃过期加载结果
let loading = false

/** 当前已加载模型信息（用于清理） */
let current: {
  entity: Entity | null
  assets: Asset[]
  cacheKeys: Array<{ url: string; type: string }>
  objectUrls: string[]
} | null = null

/** 最近一次取景框定结果，用于“重置视角” */
let lastBounds: BoundingBox | null = null

/** 轨道控制 */
const orbit = {
  radius: 8,
  minRadius: 0.01,
  maxRadius: 1000,
  theta: 0, // 水平角
  phi: Math.PI / 2 - 0.35 * 1, // 俯仰角(略高于水平面)
  target: new Vec3(0, 0, 0)
}

/* ------------------------------------------------------------------ */
/* DOM 辅助                                                             */
/* ------------------------------------------------------------------ */

const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id)
  if (!el) throw new Error(`missing #${id}`)
  return el
}

const el = {
  viewerContainer: $('viewerContainer'),
  loading: $('loading'),
  loadingText: $('loadingText'),
  error: $('error'),
  errorText: $('errorText'),
  noUrl: $('noUrl'),
  stats: $('stats'),
  statsName: $('statsName'),
  statsType: $('statsType'),
  statsCount: $('statsCount'),
  statsFps: $('statsFps'),
  fileInput: $('fileInput') as HTMLInputElement,
  dropOverlay: $('dropOverlay')
}

const setVisible = (node: HTMLElement, visible: boolean): void => {
  node.hidden = !visible
}

const hideAllPanels = (): void => {
  setVisible(el.loading, false)
  setVisible(el.error, false)
  setVisible(el.noUrl, false)
}

/* ------------------------------------------------------------------ */
/* 初始化                                                               */
/* ------------------------------------------------------------------ */

function formatFromName(name: string): FormatId | null {
  const clean = name.split('?')[0].split('#')[0].toLowerCase()
  const dot = clean.lastIndexOf('.')
  if (dot < 0) return null
  return EXT_TO_FORMAT[clean.slice(dot + 1)] ?? null
}

function init(): void {
  try {
    app = createApp()
  } catch (err) {
    showError(err instanceof Error ? err.message : String(err))
    return
  }

  createCamera()
  createLights()

  // 所有模型节点挂载到这个根下，方便整体清理/取景
  contentRoot = new Entity('models-root', app)
  app.root.addChild(contentRoot)

  app.start()

  // 统计 FPS
  let fpsAcc = 0
  let fpsFrames = 0
  app.on('update', (dt: number) => {
    fpsAcc += dt
    fpsFrames++
    if (fpsAcc >= 0.5) {
      const fps = Math.round(fpsFrames / fpsAcc)
      fpsAcc = 0
      fpsFrames = 0
      el.statsFps.textContent = `FPS: ${fps}`
    }
  })

  // 从 URL ?u= 读取模型地址；没有则展示欢迎/引导界面
  const url = new URLSearchParams(window.location.search).get('u')
  if (url && url.trim()) {
    void loadRemote(url.trim())
  } else {
    hideAllPanels()
    setVisible(el.noUrl, true)
    updateCamera()
  }
}

function createApp(): Application {
  const canvas = document.createElement('canvas')
  canvas.id = 'viewerCanvas'
  canvas.style.touchAction = 'none'
  el.viewerContainer.appendChild(canvas)

  const app = new Application(canvas, {
    graphicsDeviceOptions: {
      antialias: false, // 高斯泼溅渲染关闭 MSAA 效果更佳
      alpha: false,
      deviceTypes: ['webgl2']
    }
  })
  app.setCanvasFillMode(FILLMODE_FILL_WINDOW)
  app.setCanvasResolution(RESOLUTION_AUTO)
  window.addEventListener('resize', () => app.resizeCanvas())

  // 场景环境光（普通 GLB 网格需要灯光才能看清）
  app.scene.ambientLight = new Color(0.42, 0.42, 0.48)
  return app
}

function createCamera(): void {
  cameraEntity = new Entity('main-camera', app)
  cameraEntity.addComponent('camera', {
    projection: PROJECTION_PERSPECTIVE,
    fov: FOV,
    nearClip: 0.02,
    farClip: 20000,
    clearColor: new Color(0.02, 0.025, 0.03)
  })
  app.root.addChild(cameraEntity)
  camera = cameraEntity.camera as CameraComponent
}

function createLights(): void {
  const key = new Entity('key-light', app)
  app.root.addChild(key)
  key.addComponent('light', {
    type: 'directional',
    color: new Color(1, 0.98, 0.94),
    intensity: 1.4,
    castShadows: false
  })
  key.setEulerAngles(55, 35, 0)

  const fill = new Entity('fill-light', app)
  app.root.addChild(fill)
  fill.addComponent('light', {
    type: 'directional',
    color: new Color(0.55, 0.62, 0.9),
    intensity: 0.35,
    castShadows: false
  })
  fill.setEulerAngles(-20, -130, 0)
}

/* ------------------------------------------------------------------ */
/* 轨道相机                                                             */
/* ------------------------------------------------------------------ */

function updateCamera(): void {
  const { radius, theta, phi, target } = orbit
  const sp = Math.sin(phi)
  const cp = Math.cos(phi)
  cameraEntity.setPosition(
    target.x + radius * sp * Math.sin(theta),
    target.y + radius * cp,
    target.z + radius * sp * Math.cos(theta)
  )
  cameraEntity.lookAt(target.x, target.y, target.z)
}

function resetView(): void {
  orbit.theta = 0
  orbit.phi = Math.PI / 2 - 0.3
  if (lastBounds) {
    frameOnBounds(lastBounds)
  }
  updateCamera()
}

/** 依据包围盒设置相机距离与近远裁剪面 */
function frameOnBounds(bounds: BoundingBox): void {
  lastBounds = bounds
  const center = bounds.center
  const he = bounds.halfExtents
  const radius = Math.max(he.length(), 0.001)
  orbit.target.set(center.x, center.y, center.z)

  // 让包围球完整落入视锥并留一定余量
  const dist = (radius / Math.sin((FOV / 2) * RAD)) * 1.15
  orbit.radius = dist
  orbit.minRadius = Math.max(radius * 0.002, 0.001)
  orbit.maxRadius = radius * 40

  camera.nearClip = Math.max(dist * 0.0005, radius * 0.0005, 0.005)
  camera.farClip = Math.max(dist * 5, radius * 20, 50)
}

/** 对一棵实体树求世界空间包围盒并合并到 result */
function mergeWorldAabb(result: BoundingBox | null, root: Entity): BoundingBox {
  const visit = (node: Entity): void => {
    const anyNode = node as unknown as {
      render?: { meshInstances?: Array<{ mesh: { aabb: BoundingBox }; node: Entity }> }
      gsplat?: { resource?: { aabb?: BoundingBox } }
    }
    // 普通网格
    if (anyNode.render?.meshInstances) {
      for (const mi of anyNode.render.meshInstances) {
        TMP_BOX.setFromTransformedAabb(mi.mesh.aabb, mi.node.getWorldTransform())
        result = result ? (result.add(TMP_BOX), result) : TMP_BOX.clone()
      }
    }
    // 高斯泼溅
    if (anyNode.gsplat?.resource?.aabb) {
      TMP_BOX.setFromTransformedAabb(anyNode.gsplat.resource.aabb, node.getWorldTransform())
      result = result ? (result.add(TMP_BOX), result) : TMP_BOX.clone()
    }
    for (const child of node.children) {
      visit(child as Entity)
    }
  }
  visit(root)
  return result ?? new BoundingBox(new Vec3(0, 0, 0), new Vec3(0.5, 0.5, 0.5))
}

/* ------------------------------------------------------------------ */
/* 模型加载                                                             */
/* ------------------------------------------------------------------ */

type AssetType = ConstructorParameters<typeof Asset>[1]

/** 创建并加载资产，返回加载完成的 Asset（resolved/rejected） */
function createAndLoadAsset(name: string, type: AssetType, url: string): Promise<Asset> {
  const asset = new Asset(name, type, { url }, undefined, {
    crossOrigin: 'anonymous'
  })
  return new Promise<Asset>((resolve, reject) => {
    asset.once('load', () => resolve(asset))
    asset.once('error', (err) => reject(new Error(`资源加载失败: ${err?.message ?? err}`)))
    asset.on('progress', (loaded: number, total: number) => {
      if (loading && total > 0) {
        const pct = Math.min(100, Math.round((loaded / total) * 100))
        el.loadingText.textContent = `下载中… ${pct}%`
      }
    })
    app.assets.add(asset)
    app.assets.load(asset)
  })
}

async function loadModel(
  name: string,
  fmt: FormatId,
  url: string
): Promise<{ entity: Entity; asset: Asset; splats: number | null; meshCount: number }> {
  const assetType = fmt === 'sog' || fmt === 'ply' ? 'gsplat' : 'container'
  // 同一个 url 再次加载前清理 loader 缓存，避免拿到已销毁的旧资源
  try {
    ;(app.assets as unknown as { _loader: { clearCache: (u: string, t: string) => void } })._loader.clearCache(url, assetType)
  } catch {
    /* ignore */
  }

  if (fmt === 'sog' || fmt === 'ply') {
    const asset = await createAndLoadAsset(name, assetType, url)
    const resource = asset.resource as unknown as { aabb?: BoundingBox; numSplats?: number } | null
    const entity = new Entity(`splat-${name}`, app)
    entity.addComponent('gsplat', { asset })
    contentRoot.addChild(entity)
    const splats = resource?.numSplats ?? null
    // 等一帧，让泼溅实例完成初始化后再取景
    await nextFrame()
    if (resource?.aabb) {
      const world = new BoundingBox()
      world.center.copy(resource.aabb.center)
      world.halfExtents.copy(resource.aabb.halfExtents)
      frameOnBounds(world)
    } else {
      frameOnBounds(new BoundingBox(new Vec3(0, 0, 0), new Vec3(1, 1, 1)))
    }
    return { entity, asset, splats, meshCount: 0 }
  }

  // container: .glb / .gltf
  const asset = await createAndLoadAsset(name, assetType, url)
  const containerResource = asset.resource as unknown as {
    instantiateRenderEntity: (o?: object) => Entity
  }
  const entity = containerResource.instantiateRenderEntity({ castShadows: false })
  entity.name = `model-${name}`
  contentRoot.addChild(entity)

  // 等待两帧让实体层级与包围盒完成计算，再取景
  await nextFrame()
  await nextFrame()
  frameOnBounds(mergeWorldAabb(null, entity))

  let splatCount: number | null = null
  let meshCount = 0
  entity.forEach((node) => {
    const anyNode = node as unknown as {
      gsplat?: { resource?: { numSplats?: number } }
      render?: { meshInstances?: unknown[] }
    }
    if (anyNode.gsplat?.resource?.numSplats) {
      splatCount = (splatCount ?? 0) + (anyNode.gsplat.resource.numSplats as number)
    }
    if (anyNode.render?.meshInstances) {
      meshCount += anyNode.render.meshInstances.length
    }
  })
  const splats = splatCount
  return { entity, asset, splats, meshCount }
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve())
  })
}

/** 卸载旧模型及其资产 */
function clearCurrentModel(): void {
  if (!current) return
  for (const u of current.objectUrls) {
    URL.revokeObjectURL(u)
  }
  if (current.entity) {
    current.entity.destroy()
  }
  for (const asset of current.assets) {
    try {
      const res = asset.resource as unknown as { destroy?: () => void } | null
      if (asset.type === 'container' && res?.destroy) {
        res.destroy() // 释放 container 子资源
      }
    } catch {
      /* ignore */
    }
    try {
      asset.unload()
    } catch {
      /* ignore */
    }
    app.assets.remove(asset)
  }
  current = null
}

async function openSource(sourceName: string, fmt: FormatId, url: string, revokeUrl: string | null): Promise<void> {
  const seq = ++jobSeq
  hideAllPanels()
  setVisible(el.loading, true)
  el.loadingText.textContent = `加载 ${sourceName} …`
  loading = true

  try {
    clearCurrentModel()
    const { entity, asset, splats, meshCount } = await loadModel(sourceName, fmt, url)
    current = { entity, assets: [asset], cacheKeys: [], objectUrls: revokeUrl ? [revokeUrl] : [] }

    if (seq !== jobSeq) return // 已被新的加载请求取代

    loading = false
    hideAllPanels()
    setVisible(el.stats, true)
    el.statsName.textContent = sourceName
    el.statsType.textContent = FORMAT_LABEL[fmt]
    if (fmt === 'sog' || fmt === 'ply') {
      el.statsCount.style.display = splats != null ? '' : 'none'
      el.statsCount.textContent = splats != null ? `泼溅点: ${splats.toLocaleString()}` : ''
    } else {
      el.statsCount.style.display = ''
      el.statsCount.textContent =
        meshCount > 0 && splats != null
          ? `网格: ${meshCount} · 泼溅点: ${splats.toLocaleString()}`
          : splats != null
            ? `泼溅点: ${splats.toLocaleString()}`
            : `网格: ${meshCount}`
    }
    updateCamera()
  } catch (err) {
    loading = false
    if (revokeUrl) URL.revokeObjectURL(revokeUrl)
    if (seq === jobSeq) {
      hideAllPanels()
      showError(err instanceof Error ? err.message : String(err))
    }
  }
}

async function loadRemote(url: string): Promise<void> {
  const clean = url.split('?')[0].split('#')[0]
  const name = clean.split('/').pop() ?? url
  const fmt = formatFromName(url)
  if (!fmt) {
    showError(`无法识别文件类型：${name}\n请使用 .sog / .ply / .glb / .gltf 格式的模型地址。`)
    return
  }
  await openSource(name, fmt, url, null)
}

async function loadLocalFile(file: File): Promise<void> {
  const fmt = formatFromName(file.name)
  if (!fmt) {
    showError(`不支持的文件类型：${file.name}\n请选择 .sog / .ply / .glb / .gltf 文件。`)
    return
  }
  const objectUrl = URL.createObjectURL(file)
  await openSource(file.name, fmt, objectUrl, objectUrl)
}

function showError(message: string): void {
  hideAllPanels()
  el.errorText.textContent = message
  setVisible(el.error, true)
}

/* ------------------------------------------------------------------ */
/* 交互：旋转 / 缩放 / 拖拽打开                                        */
/* ------------------------------------------------------------------ */

function attachControls(): void {
  const container = el.viewerContainer

  // --- 指针（鼠标 + 触摸统一）---
  const pointers = new Map<number, { x: number; y: number }>()
  let rotateStart: { x: number; y: number } | null = null
  let pinchStartDist = 0
  let pinchStartRadius = orbit.radius
  const dist = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
    Math.hypot(a.x - b.x, a.y - b.y)

  container.addEventListener('pointerdown', (e: PointerEvent) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.size === 1) {
      rotateStart = { x: e.clientX, y: e.clientY }
    } else if (pointers.size === 2) {
      rotateStart = null
      const p = [...pointers.values()]
      pinchStartDist = dist(p[0], p[1])
      pinchStartRadius = orbit.radius
    }
    container.setPointerCapture(e.pointerId)
    e.preventDefault()
  })

  container.addEventListener('pointermove', (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return
    const cur = { x: e.clientX, y: e.clientY }
    pointers.set(e.pointerId, cur)

    if (pointers.size === 1 && rotateStart) {
      const dx = cur.x - rotateStart.x
      const dy = cur.y - rotateStart.y
      rotateStart = cur
      orbit.theta -= dx * 0.005
      orbit.phi -= dy * 0.005
      orbit.phi = Math.min(Math.PI - 0.05, Math.max(0.05, orbit.phi))
      updateCamera()
    } else if (pointers.size === 2) {
      const p = [...pointers.values()]
      const d = dist(p[0], p[1])
      if (pinchStartDist > 0 && d > 0) {
        orbit.radius = Math.min(orbit.maxRadius, Math.max(orbit.minRadius, pinchStartRadius * (pinchStartDist / d)))
        updateCamera()
      }
    }
    e.preventDefault()
  })

  const releasePointer = (e: PointerEvent): void => {
    pointers.delete(e.pointerId)
    if (pointers.size < 2) {
      rotateStart = null
    }
  }
  container.addEventListener('pointerup', releasePointer)
  container.addEventListener('pointercancel', releasePointer)

  // --- 滚轮缩放 ---
  container.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      e.preventDefault()
      const factor = Math.exp(e.deltaY * 0.0012)
      orbit.radius = Math.min(orbit.maxRadius, Math.max(orbit.minRadius, orbit.radius * factor))
      updateCamera()
    },
    { passive: false }
  )

  // --- 双击重置视角 ---
  container.addEventListener('dblclick', () => resetView())
}

function attachFileDrop(): void {
  const container = el.viewerContainer
  let dragDepth = 0

  const showDrop = (): void => {
    dragDepth++
    setVisible(el.dropOverlay, true)
  }
  const hideDrop = (): void => {
    dragDepth = Math.max(0, dragDepth - 1)
    if (dragDepth === 0) setVisible(el.dropOverlay, false)
  }

  container.addEventListener('dragenter', (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files')) {
      e.preventDefault()
      showDrop()
    }
  })
  container.addEventListener('dragover', (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files')) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    }
  })
  container.addEventListener('dragleave', () => hideDrop())
  container.addEventListener('drop', (e: DragEvent) => {
    e.preventDefault()
    hideDrop()
    const file = e.dataTransfer?.files?.[0]
    if (file) void loadLocalFile(file)
  })
}

function attachUi(): void {
  const openFile = (): void => el.fileInput.click()

  $('openBtn').addEventListener('click', openFile)
  $('openBtn2').addEventListener('click', openFile)
  $('resetBtn').addEventListener('click', resetView)
  $('backBtn').addEventListener('click', () => {
    // 清空查询参数回到“未指定模型”界面
    const url = new URL(window.location.href)
    url.searchParams.delete('u')
    window.history.replaceState(null, '', url.toString())
    hideAllPanels()
    setVisible(el.noUrl, true)
  })

  el.fileInput.addEventListener('change', () => {
    const file = el.fileInput.files?.[0]
    if (file) void loadLocalFile(file)
    el.fileInput.value = ''
  })
}

/* ------------------------------------------------------------------ */
/* 启动                                                                 */
/* ------------------------------------------------------------------ */

window.addEventListener('DOMContentLoaded', () => {
  attachUi()
  attachControls()
  attachFileDrop()
  init()
})
